import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateAbsence } from "@/hooks/use-absences";
import { validateAbsenceDate, ABSENCE_MIN_YEAR, ABSENCE_MAX_YEAR } from "@/lib/absence-time";

/**
 * VIB-1221: delt syk/ferie-registreringsskjema. Ekstrahert fra
 * CloseMonthAbsence (opprinnelig eneste inngang) så samme skjema kan brukes
 * fra «Meld fravær» på Mine oppgaver og lederregistrering i FravaerTab —
 * én redigeringsflyt, samme validering (overlapp-vern + attestert-måned-
 * blokk i useCreateAbsence).
 */
export function AbsenceForm({
  userId,
  onSuccess,
  onCancel,
  autoFocus = false,
}: {
  userId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Fokuser første valg når skjemaet erstatter en knapp i DOM (VIB-2536). */
  autoFocus?: boolean;
}) {
  const [type, setType] = useState<"sick" | "vacation">("sick");
  const [duration, setDuration] = useState<"full" | "partial">("full");
  // VIB-1732: syk og ferie deler nå fra/til. Tidligere hadde sykdom kun én
  // dato, så en sykemelding over flere dager måtte føres som enkeltdager.
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [partialDate, setPartialDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const createAbsence = useCreateAbsence();

  const submit = (
    start_date: string,
    end_date: string,
    start_time?: string,
    end_time?: string,
  ) => {
    createAbsence.mutate(
      { user_id: userId, type, start_date, end_date, start_time, end_time },
      {
        onSuccess: () => {
          setFromDate("");
          setToDate("");
          setPartialDate("");
          setStartTime("");
          setEndTime("");
          onSuccess?.();
        },
      },
    );
  };

  const handleSubmit = () => {
    if (dateError) return;
    if (duration === "partial") {
      if (!partialDate || !startTime || !endTime || endTime <= startTime) return;
      submit(partialDate, partialDate, startTime, endTime);
      return;
    }

    if (!fromDate || !toDate || toDate < fromDate) return;
    submit(fromDate, toDate);
  };

  // VIB-1731: fang tastefeil i årsfeltet FØR databasen avviser den, så
  // brukeren får vite hva som er galt i stedet for en teknisk feilmelding.
  const dateError =
    validateAbsenceDate(duration === "partial" ? partialDate : fromDate) ??
    (duration === "partial" ? null : validateAbsenceDate(toDate));

  const isIncomplete =
    duration === "partial"
      ? !partialDate || !startTime || !endTime || endTime <= startTime
      : !fromDate || !toDate || toDate < fromDate;

  const blocked = isIncomplete || dateError !== null;

  return (
    <div className="rounded-lg bg-muted/30 p-4">
      {/* flex-wrap + min-bredder, IKKE media queries: skjemaet brukes både i
          full sidebredde (Lukk måned) og i en smal modal. `xl:` ser bare på
          vindusbredden, så en 1280px-skjerm ga 3 faste kolonner inne i en
          535px dialog — og innholdet fløt utenfor. */}
      <div className="flex flex-wrap items-end gap-4 xl:max-w-5xl">
        <fieldset className="min-w-[13rem] flex-1 space-y-1.5">
          <legend className="text-xs font-medium text-muted-foreground">
            Type fravær
          </legend>
          <div className="grid grid-cols-2 rounded-lg border border-border/60 bg-background p-1">
            <Button
              type="button"
              size="sm"
              variant={type === "sick" ? "default" : "ghost"}
              className="h-10 gap-2 text-sm xl:h-8 xl:text-xs"
              aria-pressed={type === "sick"}
              autoFocus={autoFocus}
              onClick={() => setType("sick")}
            >
              <span className="h-2 w-2 rounded-full bg-rose-500" aria-hidden="true" />
              Syk
            </Button>
            <Button
              type="button"
              size="sm"
              variant={type === "vacation" ? "default" : "ghost"}
              className="h-10 gap-2 text-sm xl:h-8 xl:text-xs"
              aria-pressed={type === "vacation"}
              onClick={() => setType("vacation")}
            >
              <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
              Ferie
            </Button>
          </div>
        </fieldset>

        <fieldset className="min-w-[13rem] flex-1 space-y-1.5">
          <legend className="text-xs font-medium text-muted-foreground">
            Omfang
          </legend>
          <div className="grid grid-cols-2 rounded-lg border border-border/60 bg-background p-1">
            <Button
              type="button"
              size="sm"
              variant={duration === "full" ? "default" : "ghost"}
              className="h-10 text-sm xl:h-8 xl:text-xs"
              aria-pressed={duration === "full"}
              onClick={() => setDuration("full")}
            >
              Hel dag
            </Button>
            <Button
              type="button"
              size="sm"
              variant={duration === "partial" ? "default" : "ghost"}
              className="h-10 text-sm xl:h-8 xl:text-xs"
              aria-pressed={duration === "partial"}
              onClick={() => setDuration("partial")}
            >
              Deler av dagen
            </Button>
          </div>
        </fieldset>

        {duration === "partial" ? (
          <div className="flex min-w-[13rem] flex-1 flex-wrap gap-3">
            <label className="grid min-w-[8rem] flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Dato</span>
              <Input
                type="date"
                value={partialDate}
                min={`${ABSENCE_MIN_YEAR}-01-01`}
                max={`${ABSENCE_MAX_YEAR}-12-31`}
                onChange={(e) => setPartialDate(e.target.value)}
                className="h-11 w-full bg-background text-base xl:h-9 xl:text-xs"
                aria-label="Dato"
              />
            </label>
            <label className="grid min-w-[8rem] flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Fra</span>
              <Input
                type="time"
                step={900}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-11 w-full bg-background text-base xl:h-9 xl:text-xs"
                aria-label="Fra tidspunkt"
              />
            </label>
            <label className="grid min-w-[8rem] flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Til</span>
              <Input
                type="time"
                step={900}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="h-11 w-full bg-background text-base xl:h-9 xl:text-xs"
                aria-label="Til tidspunkt"
              />
            </label>
          </div>
        ) : (
          <div className="flex min-w-[13rem] flex-1 flex-wrap gap-3">
            <label className="grid min-w-[8rem] flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Fra dato</span>
              <Input
                type="date"
                value={fromDate}
                min={`${ABSENCE_MIN_YEAR}-01-01`}
                max={`${ABSENCE_MAX_YEAR}-12-31`}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-11 w-full bg-background text-base xl:h-9 xl:text-xs"
                aria-label="Fra dato"
              />
            </label>
            <label className="grid min-w-[8rem] flex-1 gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Til dato</span>
              <Input
                type="date"
                value={toDate}
                min={fromDate || `${ABSENCE_MIN_YEAR}-01-01`}
                max={`${ABSENCE_MAX_YEAR}-12-31`}
                onChange={(e) => setToDate(e.target.value)}
                className="h-11 w-full bg-background text-base xl:h-9 xl:text-xs"
                aria-label="Til dato"
              />
            </label>
          </div>
        )}
      </div>

      {dateError && (
        <p role="alert" className="mt-2 text-xs font-medium text-destructive xl:max-w-5xl">
          {dateError}
        </p>
      )}

      {duration === "partial" && (
        <p className="mt-2 text-xs text-muted-foreground xl:max-w-5xl xl:text-right">
          Fraværet trekkes fra kapasiteten etter det valgte tidsrommet, maksimalt 7,5 timer.
        </p>
      )}

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row xl:max-w-5xl xl:justify-end">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 text-sm sm:flex-none xl:h-9 xl:text-xs"
            onClick={onCancel}
          >
            Avbryt
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          className="h-11 text-sm sm:flex-none xl:h-9 xl:text-xs"
          onClick={handleSubmit}
          disabled={createAbsence.isPending || blocked}
        >
          {createAbsence.isPending ? "Registrerer …" : "Registrer fravær"}
        </Button>
      </div>
    </div>
  );
}
